/**
 * Group confirmation-number derivation.
 *
 * Front desk mints `FRO-280`, `FRO-280-2`, `FRO-280-3` for ONE reservation.
 * Everywhere the resort shows a reference for the *booking* (rather than for
 * an individual room) it must show `FRO-280` — the same value the reservation
 * confirmation and registration PDFs print. A room-suffixed number leaking
 * into the deposit modal or a printed receipt is exactly the sort of
 * disagreement that makes guests question the bill.
 *
 * This mirrors `deriveGroupConfirmationNumber` in
 * `backend/src/services/reservationPrintBuilder.ts`. Keep the two in step.
 */

/**
 * Strip a trailing room index, but ONLY when what remains is still a
 * hyphenated reference:
 *
 *   'FRO-280-2' -> 'FRO-280'   (base still has a hyphen)
 *   'FRO-280'   -> null        (nothing to strip; 'FRO' is not a reference)
 *   'CONF-1234' -> null        (never mangle an unrelated format)
 */
export function stripRoomSuffix(confirmation: string): string | null {
  const match = /^(.+)-\d+$/.exec(confirmation);
  return match && match[1].includes('-') ? match[1] : null;
}

const pickStable = (values: string[]): string =>
  [...values].sort((a, b) => a.length - b.length || a.localeCompare(b))[0];

/**
 * The single reference that identifies the whole booking.
 *
 * Deliberately order-independent: every room of a group is inserted in one
 * transaction, so they all share a `created_at` and row order is not
 * something the UI may rely on.
 */
export function deriveGroupConfirmationNumber(
  confirmationNumbers: Array<string | null | undefined>,
): string | undefined {
  // Plain-array de-duplication: admin-panel compiles to ES5, where spreading a
  // Set is not available.
  const seen: Record<string, true> = {};
  const values: string[] = [];
  for (const raw of confirmationNumbers) {
    const value = raw == null ? '' : String(raw).trim();
    if (value.length === 0 || seen[value]) continue;
    seen[value] = true;
    values.push(value);
  }

  if (values.length === 0) return undefined;
  if (values.length === 1) return values[0];

  // A reference is the base of a sibling's `base-N` number.
  const bases = values.filter((candidate) =>
    values.some((other) => other !== candidate && stripRoomSuffix(other) === candidate),
  );
  if (bases.length > 0) return pickStable(bases);

  // Every sibling was room-suffixed (the original room was removed): if they
  // all strip to the same base, that base is the group reference.
  const stripped = values.map(stripRoomSuffix);
  const firstStripped = stripped[0];
  if (firstStripped !== null && stripped.every((value) => value === firstStripped)) {
    return firstStripped;
  }

  // Distinct, unrelated references: pick deterministically.
  return pickStable(values);
}

/** Display-safe form of a single stored confirmation number. */
export function displayConfirmationNumber(
  confirmation?: string | null,
): string {
  if (!confirmation) return '';
  return stripRoomSuffix(confirmation) ?? confirmation;
}
